class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = wcmmgSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    wcmmgEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new wcmmgLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        wcmmgEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    wcmmgTheme.CompareProduct && wcmmgTheme.CompareProduct.setCompareButtonsState();
    wcmmgTheme.Wishlist && wcmmgTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    window.addEventListener("scroll", () => {
      if (wcmmgTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}

// COMPLETE FIX - Update only price, preserve card structure
(function() {
  console.log('🛠️ Size Filter Price Fix - PRESERVE CARD STRUCTURE');
  
  function updatePricesForSize(size) {
    console.log('Updating prices for size:', size);
    window.selectedSize = size;
    
    document.querySelectorAll('.m-product-item, .product-item').forEach(product => {
      const link = product.querySelector('a[href*="/products/"]');
      if (!link) return;
      
      const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0].split('#')[0];
      if (!handle) return;
      
      fetch(`/products/${handle}.js?t=${Date.now()}`)
        .then(res => res.json())
        .then(productData => {
          const variant = productData.variants.find(v => 
            v.option1 === size || v.option2 === size || v.option3 === size
          );
          
          if (variant) {
            const priceInRupees = variant.price / 100;
            const formatter = new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              minimumFractionDigits: 0
            });
            const formattedPrice = formatter.format(priceInRupees);
            
            // CRITICAL: Find the ACTUAL price element, not the card
            // Look for price inside the product card
            const priceSelectors = [
              '.m-product-price',
              '.price',
              '.product-card__price',
              '.price-item',
              '.price__regular',
              '.money',
              '[data-product-price]',
              '.current-price',
              '.price--wrapper',
              '.product-card__price-value'
            ];
            
            let priceElement = null;
            for (let selector of priceSelectors) {
              priceElement = product.querySelector(selector);
              if (priceElement) {
                console.log('Found price element with selector:', selector);
                break;
              }
            }
            
            // If still not found, look for any span/div containing price numbers
            if (!priceElement) {
              const candidates = product.querySelectorAll('span, div, p, h4, h5');
              for (let el of candidates) {
                // Make sure this is NOT the main product card
                if (el.classList.contains('m-product-card')) continue;
                
                const text = el.textContent || '';
                if (text.match(/[0-9,]+\.?[0-9]*/) && text.length < 30) {
                  // This is likely a price element
                  priceElement = el;
                  console.log('Found price element by text:', el);
                  break;
                }
              }
            }
            
            if (priceElement) {
              // SAFE UPDATE - Only change the text content
              // Find the actual text node or element that contains the number
              if (priceElement.children.length > 0) {
                // Has child elements - find the one with price
                const moneyEl = priceElement.querySelector('.money, .price-item, span');
                if (moneyEl) {
                  moneyEl.textContent = formattedPrice;
                } else {
                  // Update all text nodes
                  priceElement.childNodes.forEach(node => {
                    if (node.nodeType === 3 && node.textContent.match(/[0-9,]+/)) {
                      node.textContent = formattedPrice;
                    }
                  });
                }
              } else {
                // Simple element - just update text
                priceElement.textContent = formattedPrice;
              }
              
              console.log(`✅ Updated price for ${handle}: ${formattedPrice}`);
            } else {
              console.log('❌ Could not find price element for:', handle);
            }
          }
        })
        .catch(err => console.log('Error:', err.message));
    });
  }
  
  // Event Listeners
  document.addEventListener('change', function(e) {
    if (e.target.name && e.target.name.includes('size')) {
      const size = e.target.value;
      console.log('Size changed to:', size);
      setTimeout(() => updatePricesForSize(size), 500);
    }
  });
  
  document.addEventListener('submit', function(e) {
    if (e.target.id === 'CollectionFiltersForm') {
      const formData = new FormData(e.target);
      let size = null;
      for (let [key, value] of formData.entries()) {
        if (key.includes('size')) {
          size = value;
          break;
        }
      }
      if (size) setTimeout(() => updatePricesForSize(size), 1000);
    }
  });
  
  // URL Change Observer
  let lastUrl = location.href;
  new MutationObserver(() => {
    if (location.href !== lastUrl) {
      lastUrl = location.href;
      const params = new URLSearchParams(location.search);
      let size = null;
      params.forEach((v, k) => { if (k.includes('size')) size = v; });
      if (size) setTimeout(() => updatePricesForSize(size), 500);
    }
  }).observe(document, {subtree: true, childList: true});
  
  // Initial Load
  window.addEventListener('load', () => {
    const params = new URLSearchParams(location.search);
    let size = null;
    params.forEach((v, k) => { if (k.includes('size')) size = v; });
    if (size) {
      window.selectedSize = size;
      setTimeout(() => updatePricesForSize(size), 1000);
    }
  });
})();

// Add this function inside your existing code
function ensureSingleSizeInURL(currentSize) {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  // First, delete ALL existing size parameters
  const keysToDelete = [];
  params.forEach((_, key) => {
    if (key.includes('filter.v.option.bedding size')) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => params.delete(key));
  
  // Then, add the new size parameter (if a size is selected)
  if (currentSize) {
    params.append('filter.v.option.bedding size', currentSize);
  }
  
  // Update the URL without reloading the page
  window.history.replaceState({}, '', url.toString());
  console.log('Cleaned URL, only one size:', url.toString());
}

// Modify your 'change' event listener like this:
document.addEventListener('change', function(e) {
  if (e.target.name && e.target.name.includes('size')) {
    const selectedSize = e.target.value;
    
    // Uncheck all other size checkboxes first
    document.querySelectorAll('input[name*="size"]').forEach(cb => {
      if (cb !== e.target) cb.checked = false;
    });
    
    // Ensure the URL has only this size
    ensureSingleSizeInURL(e.target.checked ? selectedSize : null);
    
    // Update prices only if a size is checked
    if (e.target.checked) {
      setTimeout(() => updatePricesForSize(selectedSize), 300);
    }
  }
});

// Also modify the URL observer to clean up if multiple sizes appear
const urlObserver = new MutationObserver(() => {
  if (location.href !== lastUrl) {
    lastUrl = location.href;
    const params = new URLSearchParams(location.search);
    const sizeParams = [];
    
    params.forEach((value, key) => {
      if (key.includes('size')) sizeParams.push({key, value});
    });
    
    // If multiple size params, clean them up
    if (sizeParams.length > 1) {
      console.log('Cleaning up multiple sizes in URL');
      const lastSize = sizeParams[sizeParams.length - 1].value;
      ensureSingleSizeInURL(lastSize);
      
      // Also update the checkboxes to match
      document.querySelectorAll('input[name*="size"]').forEach(cb => {
        cb.checked = (cb.value === lastSize);
      });
      
      updatePricesForSize(lastSize);
    }
  }
});